import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  MessageSquareIcon,
  CheckIcon,
  HelpCircleIcon,
  UserIcon,
  ShieldIcon,
  XCircleIcon,
} from "../components/icons/FeatherIcons";
import { usePageTracking } from "@/hooks/usePageTracking";
import { APP_VERSION } from "../version";

const SupportPage: React.FC = () => {
  usePageTracking("Support", "SupportPage");
  const navigate = useNavigate();
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const faqItems = [
    {
      question: "How do I create an activity?",
      answer:
        "Tap the '+' button on the home screen, then choose 'Create with AI' for guided creation or 'Create Manually' for full control. Fill in the details like title, location, time, and group size.",
    },
    {
      question: "How do I join an activity?",
      answer:
        "Browse activities on the home screen or map view. Tap on an activity you're interested in and hit the 'Join' button. The organizer will be notified of your request.",
    },
    {
      question: "Can I edit an activity after creating it?",
      answer:
        "Yes! Go to your profile, find the activity under 'Activities', tap on it, and select 'Edit Activity'. You can modify details like time, location, and group size.",
    },
    {
      question: "How do messaging and chat work?",
      answer:
        "Once you join an activity, you'll have access to the activity chat. You can also send direct messages to other users by visiting their profile and tapping 'Message'.",
    },
    {
      question: "What if I need to cancel or leave an activity?",
      answer:
        "You can leave any activity by going to the activity page and tapping 'Leave'. If you're the organizer, you can delete the activity from the edit menu.",
    },
    {
      question: "How do I manage my privacy settings?",
      answer:
        "Go to your Profile > Settings > Privacy & Security to control who can see your activities, send you messages, and follow you.",
    },
    {
      question: "How do I report inappropriate behavior?",
      answer:
        "You can report users or activities by tapping the menu (⋯) button on their profile or activity page and selecting 'Report'. We take all reports seriously.",
    },
    {
      question: "Why can't I see someone's activities?",
      answer:
        "Their profile might be private, or you might not be following each other yet. Send them a follow request to see their activities.",
    },
    {
      question: "How do notifications work?",
      answer:
        "You'll receive notifications for activity updates, new messages, follow requests, and when someone joins your activities. Manage these in your device settings.",
    },
    {
      question: "How do I delete my account?",
      answer:
        "Currently, account deletion must be requested via email. Contact us at contact@whoisin.sg with your request and we'll process it within 48 hours.",
    },
  ];

  const supportCategories = [
    {
      title: "Getting Started",
      icon: UserIcon,
      description: "New to Who's in?? Learn the basics",
      color: "bg-blue-500",
    },
    {
      title: "Account & Privacy",
      icon: ShieldIcon,
      description: "Manage your account and privacy settings",
      color: "bg-green-500",
    },
    {
      title: "Activities & Events",
      icon: CheckIcon,
      description: "Creating, joining, and managing activities",
      color: "bg-purple-500",
    },
    {
      title: "Technical Issues",
      icon: XCircleIcon,
      description: "App bugs, crashes, and technical problems",
      color: "bg-red-500",
    },
  ];

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <div className="min-h-full bg-white">
      {/* Header */}
      <header className="bg-white px-4 py-3 flex items-center shadow-sm border-b border-gray-200 sticky-header">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 mr-4 p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Help & Support</h1>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-20 hide-scrollbar">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Quick Contact */}
          <div className="bg-gradient-to-r from-primary to-blue-500 rounded-lg p-6 text-white">
            <div className="flex items-center mb-4">
              <MessageSquareIcon className="w-8 h-8 mr-3" />
              <div>
                <h2 className="text-xl font-bold">Need Help?</h2>
                <p className="text-blue-100">We're here to assist you</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <p className="font-semibold mb-1">Email Support:</p>
                <p className="text-blue-100">contact@whoisin.sg</p>
              </div>
              <div>
                <p className="font-semibold mb-1">Response Time:</p>
                <p className="text-blue-100">Usually within 24-48 hours</p>
              </div>
            </div>
          </div>

          {/* Support Categories */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              What can we help you with?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {supportCategories.map((category, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:border-primary/30 hover:bg-primary/5 transition-colors cursor-pointer"
                >
                  <div className="flex items-start">
                    <div
                      className={`${category.color} p-2 rounded-lg mr-3 flex-shrink-0`}
                    >
                      <category.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {category.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Frequently Asked Questions */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <HelpCircleIcon className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-lg font-bold text-gray-900">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-3">
              {faqItems.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full text-left p-4 hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 pr-4">
                        {item.question}
                      </span>
                      <span
                        className={`transform transition-transform ${expandedFAQ === index ? "rotate-180" : ""}`}
                      >
                        ▼
                      </span>
                    </div>
                  </button>
                  {expandedFAQ === index && (
                    <div className="px-4 pb-4 text-gray-700 border-t border-gray-100 bg-gray-50/50">
                      <p className="pt-3 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form Alternative */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Still Need Help?
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                Can't find what you're looking for? Send us an email with:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• A detailed description of your issue</li>
                  <li>• Your username (if account-related)</li>
                  <li>• Screenshots (if applicable)</li>
                  <li>• Steps you've already tried</li>
                </ul>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="mailto:contact@whoisin.sg?subject=Who's in? Support Request"
                  className="flex-1 bg-primary text-white font-semibold py-3 px-4 rounded-lg hover:opacity-90 transition-opacity text-center"
                >
                  Send Email
                </a>
                <button
                  onClick={() => navigate("/privacy-security")}
                  className="flex-1 bg-gray-100 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Privacy & Security
                </button>
              </div>
            </div>
          </div>

          {/* App Information */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              App Information
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-semibold text-gray-900">Version:</p>
                <p className="text-gray-600">{APP_VERSION}</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Last Updated:</p>
                <p className="text-gray-600">Oct 13, 2025</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Platform:</p>
                <p className="text-gray-600">iOS & Android</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Support:</p>
                <p className="text-gray-600">24/7 Email</p>
              </div>
            </div>
          </div>

          {/* Tips for Better Experience */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              💡 Tips for a Better Experience
            </h2>
            <ul className="space-y-2 text-gray-700">
              <li>• Keep your app updated to the latest version</li>
              <li>
                • Enable location services for better activity suggestions
              </li>
              <li>• Add a profile photo to increase activity join rates</li>
              <li>• Be clear and detailed when creating activities</li>
              <li>• Respond promptly to join requests</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
